"use client";

import React, { ComponentProps, useEffect, useRef } from "react";
import styled from "styled-components";
import { positioningCSS, getBorder, getFont, getBorderRadius, getColor } from "../utils";

import Box, { BoxProps } from "../Box";
import Flex from "../Flex";
import Icon from "../Icon";
import Text from "../Text";

type InputHTMLAttributes = React.InputHTMLAttributes<HTMLInputElement>;

type Props = {
  /** A title label for the input */
  label?: string;
  autoSelect?: boolean;
  /** onSubmit handler. If provided the input is wrapped in a form */
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  /** Styles the field and message (if any) */
  inputStyle?: "success" | "warning" | "error";
  size?: "small" | "regular";
  /** A message to display under the field */
  message?: string | React.ReactNode;
  font?: string;
  icon?: ComponentProps<typeof Icon>;
  /** Button(s) to place to the right of the input field. */
  children?: React.ReactNode;
  styleOverrides?: BoxProps;
} & Omit<InputHTMLAttributes, "onSubmit" | "size">;

interface ElementProps extends Props, BoxProps {
  defaultFontSize?: string;
}

export const Element = styled.input<ElementProps>`
  width: 100%;
  border: ${getBorder("1px solid border")};
  border-radius: ${props => getBorderRadius("default", null, props)};
  background: white;
  box-sizing: border-box;
  ${props => getFont(props.font, props.defaultFontSize, props)};
  ${props => positioningCSS({ px: "8px", py: "5px", height: props.height })}
  ${positioningCSS}
  &::placeholder {
    ${props => getFont(props.font, props.defaultFontSize, props)};
    color: ${getColor("grayDark")};
  }
  &.active,
  &:focus {
    border: ${getBorder("1px solid focus")};
    outline: none;
  }
  &.error {
    border: ${getBorder("1px solid error")};
  }
  &.disabled,
  &:read-only {
    border: ${getBorder("1px solid border")};
    background: ${getColor("grayLighter")};
    color: ${getColor("grayDark")};
  }
  &[type="search"] {
    -webkit-appearance: textfield;
  }
  &[type="text"]::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }
  &[type="text"]::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }
  &[type="search"]::-webkit-search-decoration,
  &[type="search"]::-webkit-search-cancel-button,
  &[type="search"]::-webkit-search-results-button,
  &[type="search"]::-webkit-search-results-decoration {
    -webkit-appearance: none;
    display: none;
  }
`;

const Input = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      id,
      label,
      icon,
      message,
      inputStyle,
      size,
      children,
      font,
      disabled,
      readOnly,
      autoFocus,
      autoSelect,
      onSubmit,
      styleOverrides,
      ...inputAttributes
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>();
    const innerRef = (ref || inputRef) as React.MutableRefObject<HTMLInputElement>;

    useEffect(() => {
      if (autoFocus) {
        innerRef?.current?.focus();
      }
      if (autoSelect) {
        innerRef?.current?.select();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function getIcon() {
      switch (inputStyle) {
        case "error":
          return <Icon size="16" fill="error" iconId="error" mr="xs" />;
        case "success":
          return <Icon size="16" fill="success" iconId="checkmark" mr="xs" />;
        case "warning":
          return <Icon size="16" fill="warning" iconId="warning" mr="xs" />;
        default:
          return null;
      }
    }

    function renderContent() {
      const height = size === "small" ? "28px" : "36px",
        defaultFontSize = size === "small" ? "ui.small" : "ui.regular";
      return (
        <Flex alignItems="center" width="100%">
          <Element
            pl={icon ? "32px" : "8px"}
            id={id}
            role="textbox"
            ref={innerRef}
            disabled={disabled}
            readOnly={readOnly}
            className={disabled || readOnly ? "disabled" : inputStyle}
            defaultFontSize={defaultFontSize}
            height={height}
            font={font}
            {...inputAttributes}
          />
          {icon && (
            <Icon
              position="absolute"
              left="9px"
              top="50%"
              transform="translateY(-50%)"
              size="16"
              {...icon}
            />
          )}
          {children}
        </Flex>
      );
    }

    function renderForm() {
      if (!onSubmit) {
        return renderContent();
      }
      return (
        <form
          onSubmit={e => {
            e.preventDefault();
            if (onSubmit) onSubmit(e);
          }}>
          {renderContent()}
        </form>
      );
    }

    return (
      <Box data-testid={id} width="100%" {...styleOverrides}>
        {label ? (
          <Text
            as="label"
            data-testid="input-label"
            display="block"
            color="black"
            mb="4px"
            htmlFor={id}
            textAlign="left">
            {label}
          </Text>
        ) : null}
        {renderForm()}
        {message ? (
          <Flex data-testid="input-message" ml="s" flexDirection="row" alignItems="center">
            {getIcon()}
            <Text
              as="span"
              my="xs"
              font="ui.small"
              color={inputStyle === "error" ? "error" : "black"}>
              {message}
            </Text>
          </Flex>
        ) : null}
      </Box>
    );
  }
);

Input.displayName = "Input";

export default Input;
